export type ScenarioRouteLocation = Pick<Location, "pathname" | "search">;
export type ScenarioRouteSource = "path" | "query" | "fallback";

export interface ScenarioRouteResolution {
  scenarioId: string;
  source: ScenarioRouteSource;
}

const PLAY_PATH_PATTERN = /^\/play\/([^/]+)\/?$/;
const SCENARIO_PATH_PATTERN = /^\/scenarios\/([^/]+)\/?$/;

function decodePathSegment(segment: string): string | null {
  try {
    const decoded = decodeURIComponent(segment);
    return decoded.length > 0 ? decoded : null;
  } catch {
    return null;
  }
}

export function buildScenarioPlayPath(scenarioId: string): string {
  return `/play/${encodeURIComponent(scenarioId)}`;
}

export function buildScenarioLandingPath(scenarioId: string): string {
  return `/scenarios/${encodeURIComponent(scenarioId)}`;
}

export function resolveScenarioIdFromPlayPath(pathname: string): string | null {
  const match = PLAY_PATH_PATTERN.exec(pathname);
  if (match === null) return null;
  return decodePathSegment(match[1] ?? "");
}

export function resolveScenarioIdFromLandingPath(pathname: string): string | null {
  const match = SCENARIO_PATH_PATTERN.exec(pathname);
  if (match === null) return null;
  return decodePathSegment(match[1] ?? "");
}

export function resolveScenarioIdFromLocation(
  location: ScenarioRouteLocation,
  fallbackScenarioId: string,
): string {
  return resolveScenarioRouteFromLocation(location, fallbackScenarioId).scenarioId;
}

export function resolveScenarioRouteFromLocation(
  location: ScenarioRouteLocation,
  fallbackScenarioId: string,
): ScenarioRouteResolution {
  const pathScenarioId = resolveScenarioIdFromPlayPath(location.pathname);
  if (pathScenarioId !== null) {
    return { scenarioId: pathScenarioId, source: "path" };
  }

  const queryScenarioId = new URLSearchParams(location.search).get("s");
  if (queryScenarioId !== null && queryScenarioId.length > 0) {
    return { scenarioId: queryScenarioId, source: "query" };
  }

  return { scenarioId: fallbackScenarioId, source: "fallback" };
}
